// Input — player input types, keyboard capture, mouse look
// Mouse delta sanitization — consistent feel regardless of hardware/OS/scaling

const MAX_DELTA = 40; // single sub-event pixel cap — rejects OS acceleration spikes

/**
 * Extract sanitized mouse deltas from a MouseEvent.
 * - Uses getCoalescedEvents() to process individual sub-events (avoids frame-coalesced spikes)
 * - Clamps each sub-event to reject OS acceleration spikes and pointer lock re-entry garbage
 * - Normalizes by devicePixelRatio to cancel display scaling inflation (Chromium bug)
 */
export function sanitizedDeltas(e: MouseEvent): [number, number] {
  const dpr = window.devicePixelRatio || 1;
  let dx = 0;
  let dy = 0;

  // PointerEvent.getCoalescedEvents gives us the raw sub-events that were
  // batched into this frame. Process each individually so no single spike
  // dominates, and high-polling-rate mice get their full resolution.
  if ("getCoalescedEvents" in e) {
    const coalesced = (e as PointerEvent).getCoalescedEvents();
    if (coalesced.length > 0) {
      for (const sub of coalesced) {
        const sx = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, sub.movementX / dpr));
        const sy = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, sub.movementY / dpr));
        dx += sx;
        dy += sy;
      }
      return [dx, dy];
    }
  }

  // Fallback: no coalesced events (older browsers)
  dx = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, e.movementX / dpr));
  dy = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, e.movementY / dpr));
  return [dx, dy];
}

export type PlayerInput = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  ascend: boolean;
  descend: boolean;
  yaw: number; // fixed-point radians, facing direction
};

export function emptyInput(): PlayerInput {
  return { up: false, down: false, left: false, right: false, jump: false, ascend: false, descend: false, yaw: 0 };
}

// Event-driven keyboard input
export class KeyboardInput {
  private keys = new Set<string>();
  private onChange: (input: PlayerInput) => void;
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;
  private onWheel: (e: WheelEvent) => void;
  private scrollJump = false; // jump from scroll-down, held briefly for sim to catch
  private scrollTimer = 0;
  yaw = 0; // set by MouseLook

  constructor(onChange: (input: PlayerInput) => void) {
    this.onChange = onChange;
    this.onKeyDown = (e) => this.handleKey(e.code, true);
    this.onKeyUp = (e) => this.handleKey(e.code, false);
    this.onWheel = (e) => {
      if (e.deltaY > 0) {
        this.scrollJump = true;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => { this.scrollJump = false; }, 100);
        this.onChange(this.sample());
      }
    };
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("wheel", this.onWheel, { passive: true });
  }

  private handleKey(code: string, down: boolean): void {
    if (down) this.keys.add(code);
    else this.keys.delete(code);
    this.onChange(this.sample());
  }

  sample(): PlayerInput {
    return {
      up: this.keys.has("KeyW") || this.keys.has("ArrowUp"),
      down: this.keys.has("KeyS") || this.keys.has("ArrowDown"),
      left: this.keys.has("KeyA") || this.keys.has("ArrowLeft"),
      right: this.keys.has("KeyD") || this.keys.has("ArrowRight"),
      jump: this.keys.has("Space") || this.scrollJump,
      ascend: this.keys.has("KeyE"),
      descend: this.keys.has("KeyQ"),
      yaw: this.yaw,
    };
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("wheel", this.onWheel);
  }
}

// Mouse look — pointer lock, yaw/pitch from mouse movement
export class MouseLook {
  yaw = 0;   // radians, float (main thread only)
  pitch = 0; // radians, float (main thread only)
  private sensitivity = 0.002;
  private skipNext = false; // skip first event after pointer lock (often garbage)
  private onMove: (e: MouseEvent) => void;
  private onClick: () => void;
  private onLockChange: () => void;

  constructor(private canvas: HTMLCanvasElement) {
    this.onMove = (e) => {
      if (document.pointerLockElement !== canvas) return;
      if (this.skipNext) { this.skipNext = false; return; }
      const [dx, dy] = sanitizedDeltas(e);
      this.yaw += dx * this.sensitivity;
      this.pitch -= dy * this.sensitivity;
      // Clamp pitch to prevent flipping
      this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.pitch));
    };
    this.onClick = () => {
      if (document.pointerLockElement !== canvas) {
        canvas.requestPointerLock();
      }
    };
    this.onLockChange = () => {
      if (document.pointerLockElement === canvas) this.skipNext = true;
    };

    // Use mousemove (universally reliable during pointer lock).
    // In Chromium, mousemove events are PointerEvent instances, so
    // getCoalescedEvents() still works via sanitizedDeltas().
    // In Firefox, they're plain MouseEvent and the fallback path handles it.
    document.addEventListener("mousemove", this.onMove);
    canvas.addEventListener("click", this.onClick);
    document.addEventListener("pointerlockchange", this.onLockChange);
  }

  // Convert float yaw to fixed-point for the sim
  yawFixed(): number {
    return (this.yaw * 1000) | 0;
  }

  destroy(): void {
    document.removeEventListener("mousemove", this.onMove);
    this.canvas.removeEventListener("click", this.onClick);
    document.removeEventListener("pointerlockchange", this.onLockChange);
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
  }
}
